import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  FaCheckCircle,
  FaEdit,
  FaExternalLinkAlt,
  FaPaperclip,
  FaRegCopy,
  FaTimes,
  FaTrashAlt,
} from 'react-icons/fa';
import {
  formatShipmentCost,
  formatShipmentDate,
  getCarrierLabel,
  getTrackingUrl,
} from '../../../utils/shipments';
import {
  getCargoDirection,
  getCargoStatus,
  getCargoStatusOptions,
  isCargoOverdue,
} from '../../../utils/companyShipments';
import styles from './CargoTracking.module.css';

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};

const InfoRow = ({ label, children }) =>
  children ? (
    <div className={styles.infoRow}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  ) : null;

/** Sağdan açılan kargo detay paneli: bilgiler, stok kalemleri, dosyalar, geçmiş. */
const CargoDetailDrawer = ({ shipment, canEdit, busy, onClose, onEdit, onDelete, onStatusChange }) => {
  useEffect(() => {
    if (!shipment) return undefined;
    const onKeyDown = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [shipment, onClose]);

  if (!shipment) return null;

  const direction = getCargoDirection(shipment.direction);
  const status = getCargoStatus(shipment.direction, shipment.status);
  const carrierLabel = getCarrierLabel(shipment.carrier);
  const trackingUrl = getTrackingUrl(shipment.carrier, shipment.trackingNumber);
  const items = shipment.items || [];
  const attachments = shipment.attachments || [];
  const history = [...(shipment.history || [])].reverse();
  const stockApplied = (shipment.appliedItems || []).length > 0;

  const copyTracking = () =>
    navigator.clipboard
      ?.writeText(shipment.trackingNumber)
      .then(() => toast.success('Takip numarası kopyalandı'))
      .catch(() => toast.error('Kopyalanamadı'));

  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <aside
        className={styles.drawer}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cargo-detail-title"
      >
        <header className={styles.drawerHeader}>
          <div>
            <div className={styles.drawerBadges}>
              <span className={`${styles.directionBadge} ${styles[`dir_${direction.value}`]}`}>
                {direction.shortLabel}
              </span>
              <span className={`${styles.pill} ${styles[`tone_${status.tone}`]}`}>{status.label}</span>
              {isCargoOverdue(shipment) && (
                <span className={`${styles.pill} ${styles.tone_danger}`}>Gecikti</span>
              )}
            </div>
            <h2 id="cargo-detail-title">{shipment.subject}</h2>
          </div>
          <button type="button" className={styles.squareButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </header>

        <div className={styles.drawerBody}>
          {shipment.content && <p className={styles.drawerContent}>{shipment.content}</p>}

          {shipment.trackingNumber && (
            <div className={styles.trackingBox}>
              <div>
                <small>{carrierLabel || 'Takip No'}</small>
                <code>{shipment.trackingNumber}</code>
              </div>
              <button type="button" className={styles.squareButton} onClick={copyTracking} aria-label="Takip numarasını kopyala">
                <FaRegCopy />
              </button>
              {trackingUrl && (
                <a href={trackingUrl} target="_blank" rel="noopener noreferrer" className={styles.trackingLink}>
                  Sorgula <FaExternalLinkAlt />
                </a>
              )}
            </div>
          )}

          <dl className={styles.infoList}>
            <InfoRow label={direction.partyLabel}>
              {[shipment.partyName, shipment.partyOrg].filter(Boolean).join(' · ')}
            </InfoRow>
            <InfoRow label="Telefon">
              {shipment.partyPhone && <a href={`tel:${shipment.partyPhone}`}>{shipment.partyPhone}</a>}
            </InfoRow>
            <InfoRow label={direction.addressLabel}>{shipment.address}</InfoRow>
            {!shipment.trackingNumber && <InfoRow label="Kargo Firması">{carrierLabel}</InfoRow>}
            <InfoRow label="Gönderim">{formatShipmentDate(shipment.shippedAt)}</InfoRow>
            <InfoRow label="Beklenen Teslim">{formatShipmentDate(shipment.expectedAt)}</InfoRow>
            <InfoRow label="Teslim">{formatShipmentDate(shipment.deliveredAt)}</InfoRow>
            <InfoRow label="Kargo Bedeli">{formatShipmentCost(shipment.cost)}</InfoRow>
            <InfoRow label="Kaydı Açan">{shipment.ownerName}</InfoRow>
            <InfoRow label="Not">{shipment.note}</InfoRow>
          </dl>

          {items.length > 0 && (
            <section className={styles.drawerSection}>
              <h3>
                Stok Kalemleri
                <span className={`${styles.stockState} ${stockApplied ? styles.stockStateDone : ''}`}>
                  {stockApplied && <FaCheckCircle />}
                  {stockApplied ? 'Stoğa işlendi' : 'Henüz stoğa işlenmedi'}
                </span>
              </h3>
              <ul className={styles.itemList}>
                {items.map((item) => (
                  <li key={item.stockId}>
                    <span>{item.stockName}</span>
                    <strong>
                      {direction.value === 'incoming' ? '+' : '−'}
                      {item.quantity}
                    </strong>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {attachments.length > 0 && (
            <section className={styles.drawerSection}>
              <h3>Dosyalar</h3>
              <div className={styles.attachmentRow}>
                {attachments.map((file, index) => (
                  <a
                    key={file.storagePath || `${file.name}-${index}`}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.attachmentChip}
                  >
                    <FaPaperclip /> {file.name}
                  </a>
                ))}
              </div>
            </section>
          )}

          {history.length > 0 && (
            <section className={styles.drawerSection}>
              <h3>Geçmiş</h3>
              <ol className={styles.timeline}>
                {history.map((entry, index) => {
                  const entryStatus = getCargoStatus(shipment.direction, entry.status);
                  return (
                    <li key={`${entry.at}-${index}`} className={styles[`tone_${entryStatus.tone}`]}>
                      <strong>{entryStatus.label}</strong>
                      <small>
                        {formatDateTime(entry.at)}
                        {entry.by ? ` · ${entry.by}` : ''}
                      </small>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </div>

        {canEdit && (
          <footer className={styles.drawerFooter}>
            <div className={styles.quickActions}>
              {getCargoStatusOptions(shipment.direction)
                .filter((option) => option.value !== shipment.status)
                .map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`${styles.quickAction} ${styles[`tone_${option.tone}`]}`}
                    onClick={() => onStatusChange(shipment, option.value)}
                    disabled={busy}
                  >
                    {option.label}
                  </button>
                ))}
            </div>
            <div className={styles.drawerFooterActions}>
              <button type="button" className={styles.dangerButton} onClick={() => onDelete(shipment)} disabled={busy}>
                <FaTrashAlt /> Sil
              </button>
              <button type="button" className={styles.primaryButton} onClick={() => onEdit(shipment)} disabled={busy}>
                <FaEdit /> Düzenle
              </button>
            </div>
          </footer>
        )}
      </aside>
    </div>
  );
};

export default CargoDetailDrawer;
